import React from 'react';
import { ArrowLeft, Scale, FileText, FolderOpen } from 'lucide-react';

const TermsOfService = () => {
    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm py-6 sticky top-0 z-10">
                <div className="container mx-auto px-6 flex justify-between items-center">
                    <div className="flex items-center">
                        <FolderOpen className="h-7 w-7 text-teal-600 mr-2" />
                        <a href="/" className="font-semibold text-2xl text-gray-900">
                            ShiftFolder
                        </a>
                    </div>
                    <a href="/" className="text-gray-600 hover:text-teal-600 transition-colors flex items-center">
                        <ArrowLeft size={18} className="mr-2" />
                        Back to Home
                    </a>
                </div>
            </header>

            <div className="container mx-auto px-6 py-12 flex-grow">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-10 flex items-center justify-center">
                        <div className="bg-teal-100 p-4 rounded-full">
                            <Scale size={32} className="text-teal-600" />
                        </div>
                    </div>

                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-8 mb-10">
                        <div className="text-center mb-8">
                            <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
                            <p className="text-gray-500">Last updated: March, 2025</p>
                        </div>

                        <div className="prose prose-lg max-w-none">
                            <p className="text-gray-700 mb-6">
                                Please read these terms and conditions carefully before using ShiftFolder software.
                            </p>

                            <div className="bg-teal-50 p-6 rounded-lg mb-8">
                                <h2 className="text-xl font-semibold text-teal-800 mb-3">Free Software Agreement</h2>
                                <p className="text-gray-700">
                                    ShiftFolder is provided as completely free file management software for both personal and commercial use, with no hidden fees, subscription costs, or feature limitations. By using our software, you agree to the conditions outlined in these Terms.
                                </p>
                            </div>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Definitions</h2>
                            <ul className="space-y-4 mb-6 list-disc pl-5 text-gray-700">
                                <li>
                                    <strong>Company</strong> (referred to as "the Company", "We", "Us" or "Our") refers to ShiftFolder.
                                </li>
                                <li>
                                    <strong>Service</strong> refers to the ShiftFolder software application.
                                </li>
                                <li>
                                    <strong>Terms and Conditions</strong> (also referred to as "Terms") means these Terms and Conditions that form the entire agreement between You and the Company regarding the use of the Service.
                                </li>
                                <li>
                                    <strong>Website</strong> refers to ShiftFolder, accessible from <a href="https://shiftfolder.com" className="text-teal-600 hover:text-teal-800">shiftfolder.com</a>
                                </li>
                                <li>
                                    <strong>You</strong> means the individual accessing or using the Service, or the company, or other legal entity on behalf of which such individual is accessing or using the Service, as applicable.
                                </li>
                            </ul>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Acknowledgment</h2>
                            <p className="text-gray-700 mb-4">
                                These Terms and Conditions govern your use of ShiftFolder and establish the agreement between you and the Company. They outline the rights and obligations of all users regarding the use of our software.
                            </p>

                            <p className="text-gray-700 mb-4">
                                By downloading, installing, or using ShiftFolder, you confirm that you accept these Terms and agree to comply with them. If you disagree with any part of these Terms, you should not use our software.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Software License</h2>
                            <p className="text-gray-700 mb-4">
                                ShiftFolder grants you a non-exclusive, non-transferable license to use the software on your Mac device(s). This license is provided at no cost and permits both personal and commercial use.
                            </p>

                            <p className="text-gray-700 mb-4">
                                Under this license, you are prohibited from:
                            </p>

                            <ul className="space-y-2 mb-6 list-disc pl-5 text-gray-700">
                                <li>Modifying, reverse engineering, or attempting to extract the source code of the software</li>
                                <li>Redistributing, selling, leasing, or sublicensing the software</li>
                                <li>Using the software for any illegal or unauthorized purpose</li>
                                <li>Removing or altering any copyright, trademark, or other proprietary notices</li>
                            </ul>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Intellectual Property</h2>
                            <p className="text-gray-700 mb-4">
                                ShiftFolder and its original content, features, and functionality are and will remain the exclusive property of the Company. The software is protected by copyright, trademark, and other laws of both the United States and foreign countries.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">User Data and Privacy</h2>
                            <p className="text-gray-700 mb-4">
                                As detailed in our Privacy Policy, ShiftFolder processes files locally on your device. We do not collect, store, or transmit the content of your files. Your use of ShiftFolder is also subject to our Privacy Policy, which can be found on our website.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Disclaimer of Warranties</h2>
                            <p className="text-gray-700 mb-4">
                                ShiftFolder is provided "AS IS" and "AS AVAILABLE" without any warranty of any kind. We do not guarantee that the software will always be secure, error-free, or available at all times.
                            </p>

                            <p className="text-gray-700 mb-4">
                                To the maximum extent permitted by law, we disclaim all warranties, express or implied, including but not limited to:
                            </p>

                            <ul className="space-y-2 mb-6 list-disc pl-5 text-gray-700">
                                <li>Implied warranties of merchantability and fitness for a particular purpose</li>
                                <li>Any warranty that the software will meet your specific requirements</li>
                                <li>That the software will be uninterrupted, timely, secure, or error-free</li>
                                <li>That errors or defects in the software will be corrected</li>
                            </ul>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Limitation of Liability</h2>
                            <p className="text-gray-700 mb-4">
                                In no event shall the Company, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
                            </p>

                            <ul className="space-y-2 mb-6 list-disc pl-5 text-gray-700">
                                <li>Your use or inability to use the software</li>
                                <li>Any data loss or corruption resulting from use of the software</li>
                                <li>Any unauthorized access to or use of our servers and/or any information stored therein</li>
                                <li>Any bugs, viruses, or other harmful code that may be transmitted through the software</li>
                            </ul>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Governing Law</h2>
                            <p className="text-gray-700 mb-4">
                                These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Changes to These Terms</h2>
                            <p className="text-gray-700 mb-4">
                                We reserve the right to modify or replace these Terms at any time. Any material changes will be notified through the software or via email. Your continued use of ShiftFolder after any changes to the Terms constitutes your acceptance of those changes.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Termination</h2>
                            <p className="text-gray-700 mb-4">
                                We may terminate or suspend your access to ShiftFolder immediately, without prior notice or liability, for any reason, including if you breach these Terms and Conditions.
                            </p>

                            <p className="text-gray-700 mb-4">
                                Upon termination, your right to use the software will cease immediately. All provisions of the Terms which by their nature should survive termination shall survive termination, including, without limitation, ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">macOS Compatibility</h2>
                            <p className="text-gray-700 mb-4">
                                ShiftFolder is designed for macOS 11 (Big Sur) and later versions. While we strive to maintain compatibility with newer versions of macOS, we cannot guarantee that the software will function correctly on older or beta versions of the operating system.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Severability</h2>
                            <p className="text-gray-700 mb-4">
                                If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.
                            </p>

                            <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800">Contact Us</h2>
                            <p className="text-gray-700 mb-4">
                                If you have any questions about these Terms and Conditions, please contact us:
                            </p>
                            <ul className="space-y-2 mb-6 list-disc pl-5 text-gray-700">
                                <li>By email: <a href="mailto:contact@shiftfolder.com" className="text-teal-600 hover:text-teal-800">contact@shiftfolder.com</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-gray-100 rounded-xl p-6 border border-gray-200">
                        <div className="flex items-start">
                            <div className="mr-4 mt-1">
                                <FileText size={24} className="text-teal-600" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-2">Simple Terms, Transparent Policies</h3>
                                <p className="text-gray-600">
                                    At ShiftFolder, we believe in transparent, easy-to-understand policies. Our software is free to use, respects your privacy, and is designed to make file organization as simple and efficient as possible.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="bg-gray-900 text-gray-400 py-6 mt-12">
                <div className="container mx-auto px-6">
                    <div className="flex justify-center text-sm">
                        <a href="/privacy" className="hover:text-teal-400 transition-colors">Privacy Policy</a>
                        <span className="mx-3">|</span>
                        <a href="/terms" className="text-teal-400 font-medium">Terms of Use</a>
                    </div>
                    <div className="text-center mt-4 text-xs">
                        © {new Date().getFullYear()} ShiftFolder. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default TermsOfService;